import { Suspense, lazy, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ChevronDown, Building2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

// Immersive Experience
const ImmersiveExperience = lazy(() => import("@/components/immersive/ImmersiveExperience"));

// Cinematic Components
import CinematicHero from "@/components/cinematic/CinematicHero";
import VideoIntroLoader, { shouldShowVideoIntro, markVideoIntroShown } from "@/components/cinematic/VideoIntroLoader";

// Cinematic Home Components - Navbar + above-the-fold eager, rest lazy
import CinematicNavbar from "@/components/home/CinematicNavbar";
import TrustLogos from "@/components/home/TrustLogos";
// Below-the-fold: lazy load to speed up initial paint
const SocialProofStrip = lazy(() => import("@/components/trust/SocialProofStrip"));
const CinematicServicesGrid = lazy(() => import("@/components/home/CinematicServicesGrid"));
const CinematicProcess = lazy(() => import("@/components/home/CinematicProcess"));
const CinematicTestimonials = lazy(() => import("@/components/home/CinematicTestimonials"));
const CinematicFinalCTA = lazy(() => import("@/components/home/CinematicFinalCTA"));
const Footer = lazy(() => import("@/components/Footer"));


// SEO - Website Schema for homepage
import WebsiteSchema from "@/components/SEO/WebsiteSchema";
import { Helmet } from "react-helmet-async";

// FAQ Section - lazy load
const HomeFAQ = lazy(() => import("@/components/home/HomeFAQ"));

// Optimized loading placeholder
const OptimizedLoader = ({
  height = "h-20"
}: {
  height?: string;
}) => (
  <div className={`w-full ${height} flex items-center justify-center opacity-50 bg-cinema-dark`}>
    <div className="w-8 h-1 bg-white/10 rounded animate-pulse"></div>
  </div>
);

// Immersive loading screen
const ImmersiveLoader = () => (
  <div className="fixed inset-0 bg-black flex items-center justify-center">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="w-8 h-8 border-2 border-punto border-t-transparent rounded-full"
    />
  </div>
);

const Home = () => {
  const { locale } = useI18n();
  const [showVideoIntro, setShowVideoIntro] = useState(false);
  const [showImmersive, setShowImmersive] = useState(false);
  const [exitingImmersive, setExitingImmersive] = useState(false);

  // Check if we should show video intro on mount
  useEffect(() => {
    if (shouldShowVideoIntro()) {
      setShowVideoIntro(true);
    }
  }, []);

  const handleExitImmersive = () => {
    setExitingImmersive(true);
    setTimeout(() => {
      setShowImmersive(false);
      setExitingImmersive(false);
    }, 500);
  };

  // Enhanced structured data for local business
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService", "LegalService"],
    "name": "Punto Hispano",
    "legalName": "Punto Hispano Holding",
    "description": "Servicios profesionales en Suiza para hispanohablantes: gestoría, asesoría legal, seguros, vivienda, idiomas, relocation y soluciones digitales. Atención en español desde Zúrich.",
    "url": "https://puntohispano.ch",
    "logo": "https://lhfezfmidegmuwkdwjzt.supabase.co/storage/v1/object/public/img//Logo%20-%20Punto%20hispano%20(2).png",
    "image": "https://lhfezfmidegmuwkdwjzt.supabase.co/storage/v1/object/public/img//Logo%20-%20Punto%20hispano%20(2).png",
    "telephone": "+41772317288",
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Thurgauerstrasse 117, 1° Planta, Oficina 181",
      "addressLocality": "Opfikon",
      "addressRegion": "Zürich",
      "postalCode": "8152",
      "addressCountry": "CH"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "47.4245",
      "longitude": "8.5692"
    },
    "areaServed": [{
      "@type": "State",
      "name": "Zürich"
    }, {
      "@type": "Country",
      "name": "Switzerland"
    }],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "160",
      "bestRating": "5"
    },
  };

  // Show video intro first
  if (showVideoIntro) {
    return <VideoIntroLoader onComplete={() => setShowVideoIntro(false)} />;
  }

  // Show immersive experience
  if (showImmersive) {
    return (
      <>
        <AnimatePresence>
          {!exitingImmersive && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Suspense fallback={<ImmersiveLoader />}>
                <ImmersiveExperience />
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Skip button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          onClick={handleExitImmersive}
          className="fixed bottom-8 right-8 z-50 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full text-white/60 hover:text-white text-sm transition-all flex items-center gap-2"
        >
          <ChevronDown className="w-4 h-4" />
          Ver web completa
        </motion.button>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-cinema-dark text-white overflow-x-hidden">
      <Helmet>
        {(() => {
          const seo = {
            es: {
              title: 'Punto Hispano · Gestoría, Legal y Relocation en Suiza',
              desc: 'Trámites en Suiza sin dolores de cabeza: impuestos, permisos, seguros, vivienda y relocation en español. Consulta gratis 15 min. ★ 160+ reseñas Google.',
              ogTitle: 'Punto Hispano · Gestoría en Español en Suiza | Zúrich',
              ogLocale: 'es_ES',
            },
            en: {
              title: 'Punto Hispano · Spanish-Speaking Relocation & Admin in Zürich',
              desc: 'Move to Switzerland without the paperwork stress. Spanish-speaking experts in permits, taxes, insurance & housing. Free 15-min call. ★ 160+ Google reviews.',
              ogTitle: 'Punto Hispano · Spanish-Speaking Relocation & Admin in Zürich',
              ogLocale: 'en_US',
            },
            de: {
              title: 'Punto Hispano · Spanischsprachige Verwaltung & Relocation in Zürich',
              desc: 'Umzug in die Schweiz ohne Bürokratie-Stress. Spanischsprachige Experten für Bewilligungen, Steuern, Versicherungen und Wohnen. Kostenloses 15-Min-Gespräch. ★ 160+ Google-Bewertungen.',
              ogTitle: 'Punto Hispano · Spanischsprachige Verwaltung in Zürich',
              ogLocale: 'de_CH',
            },
            fr: {
              title: 'Punto Hispano · Administration et relocation hispanophone à Zurich',
              desc: 'Emménagez en Suisse sans stress administratif. Experts hispanophones en permis, impôts, assurances et logement. Appel gratuit de 15 min. ★ 160+ avis Google.',
              ogTitle: 'Punto Hispano · Administration hispanophone à Zurich',
              ogLocale: 'fr_CH',
            },
            it: {
              title: 'Punto Hispano · Consulenza e relocation ispanofona a Zurigo',
              desc: 'Trasferisciti in Svizzera senza stress burocratico. Esperti ispanofoni in permessi, tasse, assicurazioni e casa. Chiamata gratuita 15 min. ★ 160+ recensioni Google.',
              ogTitle: 'Punto Hispano · Consulenza ispanofona a Zurigo',
              ogLocale: 'it_CH',
            },
            pt: {
              title: 'Punto Hispano · Consultoria e Relocation em espanhol em Zurique',
              desc: 'Mude-se para a Suíça sem estresse burocrático. Especialistas hispanofalantes em vistos, impostos, seguros e moradia. Consulta grátis de 15 min. ★ 160+ avaliações no Google.',
              ogTitle: 'Punto Hispano · Consultoria hispanofalante em Zurique',
              ogLocale: 'pt_PT',
            },
          } as const;
          const s = seo[locale as keyof typeof seo] ?? seo.es;
          const url = locale === 'es' ? 'https://puntohispano.ch/' : `https://puntohispano.ch/${locale}/`;
          return (
            <>
              <title>{s.title}</title>
              <meta name="description" content={s.desc} />
              <meta property="og:title" content={s.ogTitle} />
              <meta property="og:description" content={s.desc} />
              <meta property="og:url" content={url} />
              <meta property="og:locale" content={s.ogLocale} />
            </>
          );
        })()}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Punto Hispano" />
        <meta name="keywords" content="gestoría suiza español, trámites suiza hispanohablantes, relocation suiza, asesoría legal suiza, impuestos suiza" />
        <meta name="theme-color" content="#0a0a0a" />
        <link rel="canonical" href={locale === 'es' ? 'https://puntohispano.ch/' : `https://puntohispano.ch/${locale}/`} />
        <link rel="alternate" hrefLang="es" href="https://puntohispano.ch/" />
        <link rel="alternate" hrefLang="de" href="https://puntohispano.ch/de/" />
        <link rel="alternate" hrefLang="fr" href="https://puntohispano.ch/fr/" />
        <link rel="alternate" hrefLang="it" href="https://puntohispano.ch/it/" />
        <link rel="alternate" hrefLang="pt" href="https://puntohispano.ch/pt/" />
        <link rel="alternate" hrefLang="en" href="https://puntohispano.ch/en/" />
        <link rel="alternate" hrefLang="x-default" href="https://puntohispano.ch/" />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      
      {/* Global Website Schema - Only on homepage */}
      <WebsiteSchema />
      


      
      {/* Cinematic Navbar */}
      <CinematicNavbar />
      
      {/* Cinematic Hero with curtain effect */}
      <CinematicHero />
      
      {/* Social Proof: real Google reviews strip (above-the-fold on first scroll) */}
      <Suspense fallback={<OptimizedLoader />}>
        <SocialProofStrip />
      </Suspense>

      {/* Trust Logos Section */}
      <TrustLogos />
      
      {/* Services Grid */}
      <section id="servicios-section" className="cv-auto">
        <Suspense fallback={<OptimizedLoader height="h-96" />}>
          <CinematicServicesGrid />
        </Suspense>
      </section>
      
      {/* About Section - Removed per user request */}
      
      {/* Process Film Strip */}
      <section id="process-section" className="cv-auto">
        <Suspense fallback={<OptimizedLoader height="h-96" />}>
          <CinematicProcess />
        </Suspense>
      </section>
      
      {/* Testimonials */}
      <section id="testimonials-section" className="cv-auto">
        <Suspense fallback={<OptimizedLoader height="h-64" />}>
          <CinematicTestimonials />
        </Suspense>
      </section>
      
      {/* FAQ */}
      <section id="faq-section" className="bg-cinema-dark cv-auto">
        <Suspense fallback={<OptimizedLoader />}>
          <HomeFAQ />
        </Suspense>
      </section>
      
      {/* Final CTA */}
      <section id="cta-section" className="cv-auto">
        <Suspense fallback={<OptimizedLoader height="h-96" />}>
          <CinematicFinalCTA />
        </Suspense>
      </section>
      
      {/* Cinematic Footer */}
      <Suspense fallback={<OptimizedLoader />}>
        <Footer />
      </Suspense>
      
      {/* Floating button to immersive experience - HIDDEN per user request */}
      {/* 
      <Link to="/experiencia">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2, type: "spring" }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-6 left-6 z-40 px-4 py-3 rounded-full bg-punto text-white shadow-lg hover:bg-punto/90 transition-colors flex items-center gap-2 group cursor-pointer"
        >
          <Building2 className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Experiencia 3D</span>
          <Sparkles className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
        </motion.div>
      </Link>
      */}
    </div>
  );
};

export default Home;
